import React, { Suspense, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useAnalytics } from './hooks/useAnalytics';
import ErrorBoundary from './components/ErrorBoundary';
import Login from './pages/Login';
import Register from './components/Register';
import ProtectedRoute from './components/ProtectedRoute';
import { AuthProvider } from './context/AuthContext';
import MainLayout from './layouts/MainLayout';
import AdminProtectedRoute from './components/AdminProtectedRoute';
import SuperAdminProtectedRoute from './components/SuperAdminProtectedRoute';
import { useAuth0 } from '@auth0/auth0-react';
import { OrganizationsProvider } from './context/OrganizationsContext';
import { setupRequestDeduplication } from './services/requestDeduplication';
import { SubscriptionProvider } from './context/SubscriptionContext';
import { setupRedashApiInterceptor } from './services/redashApi';
import posthog from 'posthog-js';
import IntercomProvider from './components/IntercomProvider';
import axios from 'axios';
import { isRefreshTokenError, dispatchRefreshTokenError } from './utils/errorHandling';

// Lazy load all page components for code splitting
const Dashboard = React.lazy(() => import('./pages/Dashboard'));
const Activity = React.lazy(() => import('./pages/Activity'));
const LocationInsights = React.lazy(() => import('./pages/LocationInsights'));
const MapTest = React.lazy(() => import('./components/MapTest'));
const Rules = React.lazy(() => import('./pages/Rules'));
const UserLookup = React.lazy(() => import('./pages/UserLookup'));
const MultipleLookup = React.lazy(() => import('./pages/MultipleLookup'));
const Lobby = React.lazy(() => import('./pages/Lobby'));
const Organizations = React.lazy(() => import('./pages/Organizations'));
const Billing = React.lazy(() => import('./pages/Billing'));
const APIKeys = React.lazy(() => import('./pages/APIKeys'));
const PolicyConfiguration = React.lazy(() => import('./pages/PolicyConfiguration'));
const Configurations = React.lazy(() => import('./pages/Configurations'));
const AvailableRules = React.lazy(() => import('./pages/AvailableRules'));
const AvailableRuleTypes = React.lazy(() => import('./pages/AvailableRuleTypes'));
const RuleTypeBuilder = React.lazy(() => import('./pages/RuleTypeBuilder'));
const Pricing = React.lazy(() => import('./pages/Pricing'));
const Subscription = React.lazy(() => import('./pages/Subscription'));
const SubscriptionSuccess = React.lazy(() => import('./pages/SubscriptionSuccess'));
const InviteUsers = React.lazy(() => import('./pages/InviteUsers'));
const HealthStatus = React.lazy(() => import('./pages/Auth0RateLimit'));
const SecurityMonitoring = React.lazy(() => import('./pages/SecurityMonitoring'));
const MyInvitations = React.lazy(() => import('./pages/MyInvitations'));
const Settings = React.lazy(() => import('./pages/Settings'));
const GeofenceManager = React.lazy(() => import('./pages/GeofenceManager'));
const SupportInvestigation = React.lazy(() => import('./pages/SupportInvestigation'));
const SuperadminLoopsTest = React.lazy(() => import('./pages/SuperadminLoopsTest'));
const TermsOfUse = React.lazy(() => import('./pages/TermsOfUse'));
const PrivacyPolicy = React.lazy(() => import('./pages/PrivacyPolicy'));
const BusinessProfile = React.lazy(() => import('./pages/BusinessProfile'));

// URL Cleanup Component
const URLCleanup: React.FC = () => {
  const { isAuthenticated, isLoading } = useAuth0();

  useEffect(() => {
    // Clean up URL after authentication is complete
    // But don't clean if email_verified parameter is present (let EmailVerificationHandler handle it)
    if (isAuthenticated && !isLoading && window.location.search) {
      const params = new URLSearchParams(window.location.search);
      const hasEmailVerified = params.get('email_verified') === 'true' || params.get('email_verified') === '1';
      
      if (!hasEmailVerified) {
      const cleanUrl = window.location.pathname;
      window.history.replaceState({}, document.title, cleanUrl);
      }
    }
  }, [isAuthenticated, isLoading]);

  useEffect(() => {
    // If Auth0 redirected back with an error in the query, route user to login cleanly
    if (window.location.search) {
      const params = new URLSearchParams(window.location.search);
      if (params.has('error') || params.has('error_description')) {
        window.history.replaceState({}, document.title, '/login');
      }
    }
  }, []);

  return null;
};

// Email Verification Handler Component
const EmailVerificationHandler: React.FC = () => {
  const { isAuthenticated, user, getAccessTokenSilently, logout, isLoading } = useAuth0();
  const hasCheckedRef = React.useRef(false);
  
  // Read URL parameter synchronously during render and store in ref
  // Also check localStorage in case the parameter was stored before Auth0 redirect
  // The ref initializer only runs once on mount, so we capture the value from the first render
  // This happens before any effects run, so we get it before URLCleanup can remove it
  const emailVerifiedRef = React.useRef<string | null>(null);
  if (emailVerifiedRef.current === null && typeof window !== 'undefined') {
    // First, check URL parameter
    const params = new URLSearchParams(window.location.search);
    const emailVerified = params.get('email_verified');
    if (emailVerified === 'true' || emailVerified === '1') {
      emailVerifiedRef.current = emailVerified;
      // Store in localStorage in case Auth0 redirects us
      localStorage.setItem('email_verified_check', 'true');
    } else {
      // Check localStorage in case parameter was stored before redirect
      const stored = localStorage.getItem('email_verified_check');
      if (stored === 'true') {
        emailVerifiedRef.current = 'true';
      }
    }
  }

  useEffect(() => {
    const checkEmailVerification = async () => {
      // Only check if user is authenticated and we haven't already checked
      if (isLoading) {
        return;
      }

      if (!isAuthenticated || !user) {
        return;
      }

      if (hasCheckedRef.current) {
        return;
      }

      // Use the ref value we captured on mount, not the current URL (which may have been cleaned)
      const emailVerified = emailVerifiedRef.current;
      
      // Check if we should verify email status:
      // 1. URL parameter is present, OR
      // 2. User's token shows email_verified=false but we want to check if it's actually verified now
      const shouldCheck = emailVerified === 'true' || emailVerified === '1' || 
                         (user?.email_verified === false && emailVerified === null);
      
      if (shouldCheck) {
        hasCheckedRef.current = true;
        
        // Clean up URL parameter now (after we've read it)
        if (emailVerified) {
          const cleanUrl = window.location.pathname;
          window.history.replaceState({}, document.title, cleanUrl);
        }
        
        try {
          const token = await getAccessTokenSilently();
          const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
          
          // Check actual email verification status from backend (Auth0 Management API)
          const response = await axios.get(`${apiUrl}/api/admin/check-email-verification`, {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });
          
          if (response.data?.email_verified === true) {
            // Only logout if the email wasn't already verified in the token
            // This prevents unnecessary logouts when visiting ?email_verified=true while already verified
            const wasAlreadyVerified = user?.email_verified === true;
            
            if (!wasAlreadyVerified) {
              // Email was just verified - try to refresh token silently first
              // If that doesn't work, then logout to get fresh token with updated status
              try {
                // Try to get a fresh token (bypass cache)
                const freshToken = await getAccessTokenSilently({
                  cacheMode: 'off',
                  authorizationParams: {
                    prompt: 'none'
                  }
                });
                
                // If we got a fresh token, reload the page to update user data
                // This avoids the logout/login flow for better UX
                if (freshToken) {
                  localStorage.removeItem('email_verified_check');
                  // Reload page to get fresh user data from Auth0
                  window.location.reload();
                  return;
                }
              } catch (tokenError) {
                // Token refresh failed (might be because user needs to re-authenticate)
                // Fall back to logout/login flow
                console.log('Token refresh failed, using logout/login flow');
              }
              
              // Fallback: Email was just verified - force logout/login to get fresh token with updated status
              // Clear the localStorage flag
              localStorage.removeItem('email_verified_check');
              setTimeout(() => {
                logout({ 
                  logoutParams: { 
                    returnTo: window.location.origin 
                  } 
                });
              }, 1000); // Small delay to show the message
            } else {
              // Email was already verified, no need to logout
              // Clear the localStorage flag
              localStorage.removeItem('email_verified_check');
            }
          }
        } catch (error: any) {
          // Handle 404 (user doesn't exist in Auth0) gracefully
          if (error.response?.status === 404) {
            // Don't try to logout/login if user doesn't exist
            return;
          }
          
          // For other errors, log but don't crash
          console.error('[EmailVerificationHandler] Error checking email verification status:', error);
        }
      }
    };
    
    checkEmailVerification();
  }, [isAuthenticated, user, getAccessTokenSilently, logout, isLoading]);

  return null;
};

// Redash API Interceptor Setup Component
const RedashApiSetup: React.FC = () => {
  const { getAccessTokenSilently, isAuthenticated, user } = useAuth0();

  useEffect(() => {
    if (isAuthenticated && user) {
      // Setup the Redash API interceptor with Auth0
      setupRedashApiInterceptor(getAccessTokenSilently, user);
    }
  }, [isAuthenticated, getAccessTokenSilently, user]);

  return null;
};

// Auth bootstrap: ensure stale Auth0 transactions don't trap the user on the Auth0 error page
const AuthBootstrap: React.FC = () => {
  const { isLoading, getAccessTokenSilently, loginWithRedirect } = useAuth0();

  useEffect(() => {
    if (isLoading) return;
    (async () => {
      try {
        await getAccessTokenSilently();
      } catch (e: any) {
        if (isRefreshTokenError(e)) {
          dispatchRefreshTokenError();
          return;
        }

        const err = (e && (e.error || e.error_description || e.message)) || '';
        const shouldReauth = ['login_required', 'consent_required', 'invalid_grant', 'interaction_required']
          .some(code => (e?.error === code) || String(err).includes(code));
        if (shouldReauth) {
          try {
            // Clear possible stale Auth0 SPA transaction keys
            [localStorage, sessionStorage].forEach(store => {
              try {
                const keys: string[] = [];
                for (let i = 0; i < store.length; i++) {
                  const k = store.key(i);
                  if (k) keys.push(k);
                }
                keys.filter(k => k.startsWith('@@auth0spajs')).forEach(k => store.removeItem(k));
              } catch {}
            });
          } catch {}
          // Force an explicit login flow
          await loginWithRedirect({ authorizationParams: { prompt: 'login' } as any });
        }
      }
    })();
  }, [isLoading, getAccessTokenSilently, loginWithRedirect]);

  return null;
};

const theme = createTheme({
  palette: {
    primary: {
      main: '#1c1b6e',
      light: '#2a2999',
      dark: '#151552',
    },
    background: {
      default: '#F6F6F6',
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: '8px',
          '&:hover': {
            backgroundColor: '#6F63B6 !important',
            color: '#ffffff !important',
            '& .MuiListItemIcon-root': {
              color: '#ffffff !important',
            },
          },
          '&.Mui-selected': {
            backgroundColor: '#EDEDED !important',
            color: 'inherit !important',
            borderRadius: '8px',
            '& .MuiListItemIcon-root': {
              color: 'inherit !important',
            },
            '&:hover': {
              backgroundColor: '#E2E2E2 !important',
              color: 'inherit !important',
              '& .MuiListItemIcon-root': {
                color: 'inherit !important',
              },
            },
          },
        },
      },
    },
  },
});

// Analytics component that runs inside Router context
const AnalyticsWrapper: React.FC = () => {
  useAnalytics();
  return null;
};

// Loading fallback component for lazy-loaded routes
const RouteLoadingFallback: React.FC = () => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      height: '100vh',
      bgcolor: '#ffffff',
    }}
  >
    <CircularProgress size={40} />
    <Typography variant="body1" sx={{ color: 'text.secondary', mt: 2 }}>
      Loading...
    </Typography>
  </Box>
);

const App: React.FC = () => {
  const excludePricing = process.env.REACT_APP_EXCLUDE_PRICING === 'true';
  
  // Setup request deduplication on app initialization
  useEffect(() => {
    setupRequestDeduplication();
  }, []);

  // Identify user in PostHog when authenticated
  const { isAuthenticated, user } = useAuth0();
  useEffect(() => {
    if (isAuthenticated && user && posthog?.identify) {
      const userId = (user as any)?.sub || (user as any)?.email || 'anonymous';
      posthog.identify(userId, {
        email: (user as any)?.email,
        $email: (user as any)?.email, // ensure PostHog person.properties.$email is populated for Attio
        userId: userId, // Required by Attio to create User records (not just People)
        name: (user as any)?.name,
        roles: (user as any)?.['https://bespot.com/roles'] || [],
      });
    } else if (!isAuthenticated && posthog?.reset) {
      posthog.reset();
    }
  }, [isAuthenticated, user]);
  
  return (
    <ErrorBoundary>
      <AuthProvider>
        <ThemeProvider theme={theme}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <IntercomProvider>
            <OrganizationsProvider>
            <SubscriptionProvider>
            <Router>
            <URLCleanup />
            <EmailVerificationHandler />
            <RedashApiSetup />
            <AuthBootstrap />
            <AnalyticsWrapper />
            <Suspense fallback={<RouteLoadingFallback />}>
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/home" element={<ProtectedRoute><MainLayout><Lobby /></MainLayout></ProtectedRoute>} />
              <Route path="/settings" element={<ProtectedRoute><MainLayout><Settings /></MainLayout></ProtectedRoute>} />
              <Route path="/billing" element={<ProtectedRoute><AdminProtectedRoute><MainLayout><Billing /></MainLayout></AdminProtectedRoute></ProtectedRoute>} />
              <Route path="/api-keys" element={<ProtectedRoute><AdminProtectedRoute><MainLayout><APIKeys /></MainLayout></AdminProtectedRoute></ProtectedRoute>} />
              <Route path="/invite-users" element={<ProtectedRoute><AdminProtectedRoute><MainLayout><InviteUsers /></MainLayout></AdminProtectedRoute></ProtectedRoute>} />
              <Route path="/my-collaborations" element={<ProtectedRoute><MainLayout><MyInvitations /></MainLayout></ProtectedRoute>} />
              <Route path="/my-invitations" element={<Navigate to="/my-collaborations" replace />} />
              <Route path="/policy-configuration" element={<ProtectedRoute><AdminProtectedRoute><MainLayout><PolicyConfiguration /></MainLayout></AdminProtectedRoute></ProtectedRoute>} />
              <Route path="/geofence-manager" element={<ProtectedRoute><AdminProtectedRoute><MainLayout><GeofenceManager /></MainLayout></AdminProtectedRoute></ProtectedRoute>} />
              <Route path="/configurations" element={<ProtectedRoute><SuperAdminProtectedRoute><MainLayout><Configurations /></MainLayout></SuperAdminProtectedRoute></ProtectedRoute>} />
              <Route path="/available-rules" element={<ProtectedRoute><SuperAdminProtectedRoute><MainLayout><AvailableRules /></MainLayout></SuperAdminProtectedRoute></ProtectedRoute>} />
              <Route path="/available-rule-types" element={<ProtectedRoute><SuperAdminProtectedRoute><MainLayout><AvailableRuleTypes /></MainLayout></SuperAdminProtectedRoute></ProtectedRoute>} />
              <Route path="/rule-type-builder" element={<ProtectedRoute><SuperAdminProtectedRoute><MainLayout><RuleTypeBuilder /></MainLayout></SuperAdminProtectedRoute></ProtectedRoute>} />
              <Route path="/health-status-monitoring" element={<ProtectedRoute><SuperAdminProtectedRoute><MainLayout><HealthStatus /></MainLayout></SuperAdminProtectedRoute></ProtectedRoute>} />
              <Route path="/security-monitoring" element={<ProtectedRoute><SuperAdminProtectedRoute><MainLayout><SecurityMonitoring /></MainLayout></SuperAdminProtectedRoute></ProtectedRoute>} />
              <Route path="/support-investigation" element={<ProtectedRoute><SuperAdminProtectedRoute><MainLayout><SupportInvestigation /></MainLayout></SuperAdminProtectedRoute></ProtectedRoute>} />
              <Route path="/superadmin/loops-test" element={<ProtectedRoute><SuperAdminProtectedRoute><MainLayout><SuperadminLoopsTest /></MainLayout></SuperAdminProtectedRoute></ProtectedRoute>} />
              {!excludePricing && (
                <Route path="/pricing" element={<ProtectedRoute><MainLayout><Pricing /></MainLayout></ProtectedRoute>} />
              )}
              <Route path="/subscription" element={<ProtectedRoute><MainLayout><Subscription /></MainLayout></ProtectedRoute>} />
              <Route path="/subscription-success" element={<ProtectedRoute><MainLayout><SubscriptionSuccess /></MainLayout></ProtectedRoute>} />
              <Route path="/business-profile" element={<ProtectedRoute><MainLayout><BusinessProfile /></MainLayout></ProtectedRoute>} />
              <Route path="/" element={<Navigate to="/home" replace />} />
              <Route path="/lobby" element={<Navigate to="/home" replace />} />
              <Route path="/overview" element={<ProtectedRoute><MainLayout><Dashboard /></MainLayout></ProtectedRoute>} />
              <Route path="/activity" element={
                <ProtectedRoute>
                  <MainLayout><Activity /></MainLayout>
                </ProtectedRoute>
              } />
              <Route path="/rules" element={
                <ProtectedRoute>
                  <MainLayout><Rules /></MainLayout>
                </ProtectedRoute>
              } />
              <Route path="/users" element={
                <ProtectedRoute>
                  <MainLayout><UserLookup /></MainLayout>
                </ProtectedRoute>
              } />
              <Route path="/user-lookup" element={<Navigate to="/users" replace />} />
              <Route path="/investigation" element={
                <ProtectedRoute>
                  <MainLayout><MultipleLookup /></MainLayout>
                </ProtectedRoute>
              } />
              <Route path="/multiple-lookup" element={<Navigate to="/investigation" replace />} />
              <Route path="/location-insights" element={
                <ProtectedRoute>
                  <MainLayout><LocationInsights /></MainLayout>
                </ProtectedRoute>
              } />
              <Route path="/map-test" element={
                <ProtectedRoute>
                  <MainLayout><MapTest /></MainLayout>
                </ProtectedRoute>
              } />
              <Route path="/workspaces" element={<ProtectedRoute><AdminProtectedRoute><MainLayout><Organizations /></MainLayout></AdminProtectedRoute></ProtectedRoute>} />
              <Route path="/terms-of-use" element={<ProtectedRoute><MainLayout><TermsOfUse /></MainLayout></ProtectedRoute>} />
              <Route path="/privacy-policy" element={<ProtectedRoute><MainLayout><PrivacyPolicy /></MainLayout></ProtectedRoute>} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
            </Suspense>
            </Router>
            </SubscriptionProvider>
            </OrganizationsProvider>
            </IntercomProvider>
          </LocalizationProvider>
        </ThemeProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
};

export default App;
